import { api, errText, type Account } from '../api'
import { beep, buzz, fanfare } from '../lib/audio'
import { dayKey, yesterdayKey } from '../lib/dates'
import { squareBlob } from '../lib/image'
import { pickOne, randInt, shuffle } from '../lib/random'
import { T } from '../strings'
import { initData, isInsideMax, requestContact } from '../max'
import { takeInviteFromUrl, takeRestoreFromUrl } from '../lib/invite'
import { forgetRestoreKey, keepRestoreKey, loadRestoreKey } from '../lib/restoreKey'
import { load, save } from './storage'
import { mergeProgress } from '../../shared/progress'
import {
  BOSS_HP,
  MASTERY_MAX,
  MAX_HEARTS,
  RACE_SECONDS,
  factKey,
  scopeFacts,
  type GameState,
  type Mode,
  type Pair,
  type Profile,
  type Progress,
  type Question,
  type Scope,
  type Screen,
  type Settings,
  type SyncState,
} from './types'

/** Порядок раундов одной игры. Всегда одинаковый — так предсказуемее. */
const PATTERN: Mode[] = ['choice', 'keypad', 'choice', 'match', 'keypad', 'race', 'boss']

const FEEDBACK_OK_MS = 750
const FEEDBACK_BAD_MS = 1600
const BOSS_DEFEAT_MS = 900
/** Потолок одного засчитанного отрезка, чтобы забытая вкладка не раздула статистику. */
const MAX_BANKED_SECONDS = 15 * 60
/** Прогресс уходит на сервер пачкой: во время игры ответы идут часто. */
const PUSH_DEBOUNCE_MS = 2500
/** Имя и возраст правят посимвольно — ждём паузу в наборе. */
const PROFILE_DEBOUNCE_MS = 700

type Listener = () => void

function idleState(
  settings: Settings,
  profile: Profile,
  progress: Progress,
  account: Account | null = null,
  sync: SyncState = 'idle',
): GameState {
  return {
    settings,
    profile,
    progress,
    account,
    sync,
    inviteNote: null,
    restoreAsk: null,
    restoreNote: null,
    screen: 'home',
    rounds: [],
    idx: 0,
    hearts: MAX_HEARTS,
    right: 0,
    wrong: 0,
    combo: 0,
    sessionXp: 0,
    lastXp: 10,
    q: null,
    fb: null,
    input: '',
    mLeft: null,
    mDone: [],
    raceLeft: RACE_SECONDS,
    raceScore: 0,
    bossHp: BOSS_HP,
    tricky: [],
    locked: false,
  }
}

export class GameStore {
  private state: GameState
  private listeners = new Set<Listener>()
  private raceTimer: ReturnType<typeof setInterval> | null = null
  private timeouts = new Set<ReturnType<typeof setTimeout>>()
  private sessionStart: number | null = null

  private pushTimer: ReturnType<typeof setTimeout> | null = null
  private profileTimer: ReturnType<typeof setTimeout> | null = null
  /** Код из ссылки-приглашения: забираем сразу, пока адрес не изменили. */
  private pendingInvite = takeInviteFromUrl()
  /** Ключ из ссылки восстановления — по той же причине забираем сразу. */
  private pendingRestore = takeRestoreFromUrl()

  constructor() {
    // Ключ из адреса переживёт офлайн и закрытую вкладку: сам адрес мы уже
    // почистили, и второго шанса прочитать его не будет.
    if (this.pendingRestore) keepRestoreKey(this.pendingRestore, Date.now())

    const { settings, profile, progress } = load()
    this.state = idleState(settings, profile, progress)
    void this.bootstrap()
  }

  // ------------------------------------------------------------------- сервер

  /**
   * Первый обмен с сервером: узнаём, кто мы, и сливаем локальный прогресс
   * с серверным. Внутри MAX сразу входим по подписанным данным мини-приложения.
   */
  private async bootstrap(): Promise<void> {
    try {
      const result = isInsideMax()
        ? await api.loginMax(initData()).catch(() => api.me())
        : await api.me()

      // Пустой серверный аккаунт — значит, играли локально: заливаем своё.
      const fresh = !result.player.firstName && result.player.age === null && !result.player.avatarUrl
      this.applyAccount(result.player, { keepLocalProfile: fresh })

      const merged = mergeProgress(result.progress, this.state.progress)
      this.set({ progress: merged, sync: 'idle' })
      this.persist()

      const localAddedSomething = JSON.stringify(merged) !== JSON.stringify(result.progress)
      if (localAddedSomething) {
        await this.push()
      }

      if (fresh && (this.state.profile.firstName || this.state.profile.age !== null)) {
        await this.sendProfile()
      }

      // Сначала ключ: локальный прогресс уже уехал на сервер выше, и при
      // восстановлении он штатно вольётся в найденный аккаунт.
      await this.askRestore()
      await this.redeemInvite()
    } catch {
      // Нет сети или сервер лежит — играем локально, синхронизируемся позже.
      this.set({ sync: 'offline' })
    }
  }

  /**
   * Пришли по ссылке-приглашению: добавляем друга сами и открываем вкладку
   * «Друзья» — ребёнку не нужно понимать, что такое код.
   */
  private async redeemInvite(): Promise<void> {
    const code = this.pendingInvite
    this.pendingInvite = ''
    if (!code) return

    try {
      const { friend } = await api.addFriend({ code })
      this.set({ inviteNote: T.friendAdded(friend.name), screen: 'friends' })
    } catch (error) {
      this.set({ inviteNote: errText(error, 'Не получилось добавить друга по ссылке') })
    }
  }

  dismissInviteNote = (): void => {
    this.set({ inviteNote: null })
  }

  /**
   * Пришли по ссылке с ключом: спрашиваем, чей прогресс вернуть, и открываем
   * «Профиль». Молча переключать аккаунт нельзя — ссылка могла прийти чужая.
   */
  private async askRestore(): Promise<void> {
    // Ключ из адреса важнее сохранённого: ссылка могла прийти новая.
    const code = this.pendingRestore || loadRestoreKey(Date.now())
    this.pendingRestore = ''
    if (!code || isInsideMax()) {
      // Внутри MAX аккаунт и так свой: автовход при следующем запуске поглотит
      // восстановленный и обнулит ключ, сохранённый родителями.
      return
    }

    const error = await this.peekRestore(code)
    if (error) {
      this.set({ restoreNote: error, screen: 'profile' })
    }
  }

  /**
   * Спрашивает сервер, чей это ключ, и показывает подтверждение. Через него
   * идёт и ссылка, и ручной ввод: переключение аккаунта необратимо, а ключ
   * могли дать чужой — например, брата.
   */
  peekRestore = async (code: string): Promise<string | null> => {
    try {
      const { name, pct } = await api.restorePeek(code)
      this.set({ restoreAsk: { code, name, pct }, restoreNote: null, screen: 'profile' })
      return null
    } catch (error) {
      return errText(error, 'Ключ не подошёл')
    }
  }

  dismissRestoreNote = (): void => {
    this.set({ restoreNote: null })
  }

  dismissRestoreAsk = (): void => {
    forgetRestoreKey()
    this.set({ restoreAsk: null, restoreNote: null })
  }

  /** Вход по ключу. Возвращает текст ошибки или null при успехе. */
  restore = async (code: string): Promise<string | null> => {
    try {
      const result = await api.restore(code)
      forgetRestoreKey()
      this.set({ restoreAsk: null, restoreNote: null })
      await this.applyLogin(result.player, result.progress)
      return null
    } catch (error) {
      return errText(error, 'Не получилось вернуть прогресс')
    }
  }

  /** Переносит серверный аккаунт в состояние (профиль, звук, ссылка на фото). */
  private applyAccount(account: Account, options: { keepLocalProfile?: boolean } = {}): void {
    const profile: Profile = options.keepLocalProfile
      ? { ...this.state.profile, avatarUrl: account.avatarUrl ?? this.state.profile.avatarUrl }
      : {
          firstName: account.firstName,
          lastName: account.lastName,
          age: account.age,
          avatarUrl: account.avatarUrl,
        }

    this.set({
      account,
      profile,
    })
    this.persist()
  }

  private schedulePush(): void {
    if (this.pushTimer !== null) clearTimeout(this.pushTimer)
    this.pushTimer = setTimeout(() => {
      this.pushTimer = null
      void this.push()
    }, PUSH_DEBOUNCE_MS)
  }

  /** Отправляет снапшот прогресса; сервер возвращает слитый результат. */
  private async push(): Promise<void> {
    if (this.pushTimer !== null) {
      clearTimeout(this.pushTimer)
      this.pushTimer = null
    }
    if (!this.state.account) return

    this.set({ sync: 'saving' })
    try {
      const { progress } = await api.putProgress(this.state.progress)
      // Пока шёл запрос, ребёнок мог ответить ещё раз — сливаем, а не затираем.
      this.set({ progress: mergeProgress(progress, this.state.progress), sync: 'idle' })
      this.persist()
    } catch {
      this.set({ sync: 'offline' })
    }
  }

  private scheduleProfileSend(): void {
    if (this.profileTimer !== null) clearTimeout(this.profileTimer)
    this.profileTimer = setTimeout(() => {
      this.profileTimer = null
      void this.sendProfile()
    }, PROFILE_DEBOUNCE_MS)
  }

  private async sendProfile(): Promise<void> {
    if (!this.state.account) return

    const { firstName, lastName, age } = this.state.profile
    try {
      const { player } = await api.updateMe({ firstName, lastName, age })
      this.set({ account: player, sync: 'idle' })
    } catch {
      this.set({ sync: 'offline' })
    }
  }

  // ------------------------------------------------------- вход и профиль MAX

  /** Вход через мессенджер. Возвращает текст ошибки или null при успехе. */
  loginWithMax = async (): Promise<string | null> => {
    if (!isInsideMax()) {
      return 'Откройте «Умножайку» внутри мессенджера MAX'
    }

    try {
      const result = await api.loginMax(initData())
      this.applyAccount(result.player)
      this.set({ progress: mergeProgress(result.progress, this.state.progress) })
      this.persist()
      await this.push()
      return null
    } catch (error) {
      return errText(error, 'Не удалось войти через MAX')
    }
  }

  /** Вход состоялся: забираем аккаунт и сливаем с ним то, что нажито здесь. */
  applyLogin = async (account: Account, progress: Progress): Promise<void> => {
    this.applyAccount(account)
    this.set({ progress: mergeProgress(progress, this.state.progress) })
    this.persist()
    await this.push()
  }

  /** Просит номер телефона у MAX — по нему друзья находят друг друга. */
  linkPhone = async (): Promise<string | null> => {
    try {
      const contact = await requestContact()
      if (!contact) {
        return 'Номер доступен только внутри MAX'
      }

      const { player } = await api.setPhone(contact)
      this.set({ account: player })
      return null
    } catch (error) {
      return errText(error, 'Не удалось получить номер')
    }
  }

  uploadAvatar = async (file: File): Promise<string | null> => {
    try {
      const blob = await squareBlob(file)
      const { player } = await api.uploadAvatar(blob)
      this.applyAccount(player)
      return null
    } catch (error) {
      return errText(error, 'Не удалось загрузить фото')
    }
  }

  removeAvatar = async (): Promise<string | null> => {
    try {
      const { player } = await api.deleteAvatar()
      this.applyAccount(player)
      return null
    } catch (error) {
      return errText(error, 'Не удалось убрать фото')
    }
  }

  // ---------------------------------------------------------------- plumbing

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  getSnapshot = (): GameState => this.state

  private set(patch: Partial<GameState>): void {
    this.state = { ...this.state, ...patch }
    for (const l of this.listeners) l()
  }

  private persist(): void {
    const { settings, profile, progress } = this.state
    save({ settings, profile, progress })
  }

  private setProgress(patch: Partial<Progress>): void {
    this.set({ progress: { ...this.state.progress, ...patch } })
    this.persist()
    this.schedulePush()
  }

  private later(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      this.timeouts.delete(id)
      fn()
    }, ms)
    this.timeouts.add(id)
  }

  private clearTimers(): void {
    if (this.raceTimer !== null) {
      clearInterval(this.raceTimer)
      this.raceTimer = null
    }
    for (const id of this.timeouts) clearTimeout(id)
    this.timeouts.clear()
  }

  dispose(): void {
    this.clearTimers()
    this.bankTime()
  }

  /** Вкладка скрыта: останавливаем часы, простой не идёт в зачёт. */
  pause = (): void => {
    if (this.raceTimer !== null) {
      clearInterval(this.raceTimer)
      this.raceTimer = null
    }
    this.bankTime()
    // Вкладку могут закрыть насовсем — не ждём дебаунс.
    void this.push()
  }

  resume = (): void => {
    if (this.state.screen !== 'session') return
    this.sessionStart = Date.now()
    if (this.state.q?.mode === 'race' && this.state.raceLeft > 0 && !this.state.locked) {
      this.startRaceClock()
    }
  }

  // ------------------------------------------------------ настройки и профиль

  /** Зона таблицы живёт только локально — серверу её знать незачем. */
  setScope = (scope: Scope): void => {
    this.set({ settings: { ...this.state.settings, scope } })
    this.persist()
  }

  updateProfile = (patch: Partial<Profile>): void => {
    this.set({ profile: { ...this.state.profile, ...patch } })
    this.persist()
    this.scheduleProfileSend()
  }

  // ------------------------------------------------------------- question gen

  /**
   * Взвешенный выбор: чем хуже факт освоен, тем чаще выпадает, а угол 6–10
   * получает дополнительный вес — это самая трудная часть таблицы.
   */
  private pickFact(exclude: string[] = []): { a: number; b: number } {
    const { mastery } = this.state.progress
    const facts = scopeFacts(this.state.settings.scope)
    const pool: Array<[number, number]> = []
    for (const [a, b] of facts) {
      const k = factKey(a, b)
      if (exclude.includes(k)) continue
      const lvl = mastery[k] ?? 0
      const weight = Math.max(1, 7 - lvl * 2) + (a >= 6 && b >= 6 ? 2 : 0)
      for (let i = 0; i < weight; i++) pool.push([a, b])
    }
    // Пустой пул бывает, когда исключили всю зону: берём любой её факт.
    const [a, b] = pickOne(pool) ?? facts[0] ?? [7, 8]
    return { a, b }
  }

  /** Наименьший факт с ещё не занятым произведением — страховка раунда пар. */
  private firstFactWithNewProduct(products: Set<number>): { a: number; b: number } | null {
    for (const [a, b] of scopeFacts(this.state.settings.scope)) {
      if (!products.has(a * b)) return { a, b }
    }
    return null
  }

  private makeQuestion(mode: Mode): Question {
    const fact = this.pickFact()
    const answer = fact.a * fact.b
    const q: Question = { mode, a: fact.a, b: fact.b, answer, opts: [], pairs: [], rights: [] }

    if (mode === 'choice' || mode === 'race' || mode === 'boss') {
      const set = new Set<number>([answer])
      const candidates = [
        answer + fact.a,
        answer - fact.a,
        answer + fact.b,
        answer - fact.b,
        answer + 1,
        answer - 1,
        fact.a * (fact.b + 1),
        (fact.a + 1) * fact.b,
      ].filter((v) => v > 0)
      let guard = 0
      while (set.size < 4 && guard++ < 50) {
        const v = pickOne(candidates)
        if (v !== undefined) set.add(v)
      }
      // На мелких фактах (2×2) пул отвлекающих может иссякнуть — добираем соседями.
      for (let v = answer + 2; set.size < 4; v++) set.add(v)
      q.opts = shuffle([...set]).slice(0, 4)
      if (!q.opts.includes(answer)) q.opts[randInt(0, 3)] = answer
    }

    if (mode === 'match') {
      const used: string[] = []
      const products = new Set<number>()
      const pairs: Pair[] = []
      for (let i = 0; i < 4; i++) {
        // Два факта с общим произведением (8×5 и 5×8) сделали бы правую колонку
        // неоднозначной, а раунд — непроходимым. Держим произведения разными.
        let p: { a: number; b: number } | null = null
        for (let tries = 0; tries < 40; tries++) {
          const candidate = this.pickFact(used)
          if (!products.has(candidate.a * candidate.b)) {
            p = candidate
            break
          }
        }
        p = p ?? this.firstFactWithNewProduct(products)
        if (!p) break
        used.push(factKey(p.a, p.b), factKey(p.b, p.a))
        products.add(p.a * p.b)
        pairs.push({ a: p.a, b: p.b, r: p.a * p.b })
      }
      q.pairs = pairs
      q.rights = shuffle(pairs.map((p) => p.r))
      if (pairs.length > 0) {
        q.a = pairs[0].a
        q.b = pairs[0].b
        q.answer = pairs[0].r
      }
    }

    return q
  }

  // ------------------------------------------------------------- session flow

  startSession = (): void => {
    this.clearTimers()
    this.markStreak()
    this.sessionStart = Date.now()

    const rounds = [...PATTERN]
    const q = this.makeQuestion(rounds[0])

    this.set({
      screen: 'session',
      rounds,
      idx: 0,
      q,
      hearts: MAX_HEARTS,
      right: 0,
      wrong: 0,
      combo: 0,
      sessionXp: 0,
      tricky: [],
      input: '',
      fb: null,
      mLeft: null,
      mDone: [],
      bossHp: BOSS_HP,
      raceLeft: RACE_SECONDS,
      raceScore: 0,
      locked: false,
    })

    if (rounds[0] === 'race') this.runRace()
  }

  private runRace(): void {
    this.set({ raceLeft: RACE_SECONDS, raceScore: 0 })
    this.startRaceClock()
  }

  private startRaceClock(): void {
    if (this.raceTimer !== null) clearInterval(this.raceTimer)
    this.raceTimer = setInterval(() => {
      const left = this.state.raceLeft - 1
      if (left <= 0) {
        this.set({ raceLeft: 0 })
        this.next()
      } else {
        this.set({ raceLeft: left })
      }
    }, 1000)
  }

  private next(): void {
    this.clearTimers()
    const idx = this.state.idx + 1
    if (idx >= this.state.rounds.length) {
      this.finish()
      return
    }
    const mode = this.state.rounds[idx]
    this.set({
      idx,
      q: this.makeQuestion(mode),
      input: '',
      fb: null,
      mLeft: null,
      mDone: [],
      bossHp: BOSS_HP,
      locked: false,
    })
    if (mode === 'race') this.runRace()
  }

  private finish(): void {
    this.clearTimers()
    const { sessionXp, right, wrong, progress } = this.state
    this.bankTime(right + wrong)
    this.set({ screen: 'result' })
    this.setProgress({ xp: progress.xp + sessionXp })
    fanfare()
    // Итог игры — самый важный момент для ленты друзей, отправляем сразу.
    void this.push()
  }

  goHome = (): void => this.go('home')

  go = (screen: Screen): void => {
    if (screen === this.state.screen) return
    if (this.state.screen === 'session') {
      this.clearTimers()
      this.bankTime(this.state.right + this.state.wrong)
    }
    this.set({ screen, fb: null, locked: false })
  }

  // ------------------------------------------------------------------ grading

  private bumpMastery(key: string, ok: boolean): Record<string, number> {
    const mastery = { ...this.state.progress.mastery }
    const cur = mastery[key] ?? 0
    mastery[key] = ok ? Math.min(MASTERY_MAX, cur + 1) : Math.max(0, cur - 2)
    return mastery
  }

  private markStreak(): void {
    const today = dayKey()
    const { lastPlayed, streak } = this.state.progress
    if (lastPlayed === today) return
    const next = lastPlayed === yesterdayKey() ? streak + 1 : 1
    this.setProgress({ streak: next, lastPlayed: today })
  }

  /** Складываем время игры (и число ответов) в историю за сегодня. */
  private bankTime(answers = 0): void {
    const elapsed =
      this.sessionStart === null ? 0 : Math.round((Date.now() - this.sessionStart) / 1000)
    // Вкладка, забытая на недорешённом раунде, — это не час занятий.
    const seconds = Math.min(elapsed, MAX_BANKED_SECONDS)
    this.sessionStart = null
    if (seconds <= 0 && answers <= 0) return
    const key = dayKey()
    const history = { ...this.state.progress.history }
    const cur = history[key] ?? { seconds: 0, answers: 0 }
    history[key] = { seconds: cur.seconds + seconds, answers: cur.answers + answers }
    this.setProgress({ history })
  }

  grade = (value: number | string): void => {
    const { q, locked } = this.state
    if (!q || locked) return

    const key = factKey(q.a, q.b)
    const ok = Number(value) === q.answer

    if (ok) {
      beep(720, 0.1)
      const mastery = this.bumpMastery(key, true)

      if (q.mode === 'race') {
        this.setProgress({ mastery })
        this.set({
          raceScore: this.state.raceScore + 1,
          right: this.state.right + 1,
          sessionXp: this.state.sessionXp + 5,
          q: this.makeQuestion('race'),
        })
        return
      }

      if (q.mode === 'boss') {
        const hp = this.state.bossHp - 1
        this.setProgress({ mastery })
        this.set({
          bossHp: hp,
          right: this.state.right + 1,
          combo: this.state.combo + 1,
          sessionXp: this.state.sessionXp + 15,
        })
        if (hp <= 0) {
          this.set({ locked: true, fb: 'ok', lastXp: 30 })
          this.later(() => this.finish(), BOSS_DEFEAT_MS)
        } else {
          this.set({ q: this.makeQuestion('boss') })
        }
        return
      }

      const gain = 10 + Math.min(10, this.state.combo * 2)
      this.setProgress({ mastery })
      this.set({
        right: this.state.right + 1,
        combo: this.state.combo + 1,
        sessionXp: this.state.sessionXp + gain,
        lastXp: gain,
        fb: 'ok',
        locked: true,
      })
      this.later(() => this.next(), FEEDBACK_OK_MS)
      return
    }

    // --- неверный ответ
    buzz()
    const tricky = this.state.tricky.includes(key)
      ? this.state.tricky
      : [...this.state.tricky, key]
    // В гонке ошибка стоит времени, а не жизни.
    const hearts = q.mode === 'race' ? this.state.hearts : this.state.hearts - 1

    this.setProgress({ mastery: this.bumpMastery(key, false) })
    this.set({
      wrong: this.state.wrong + 1,
      combo: 0,
      tricky,
      hearts,
      fb: 'bad',
      locked: true,
      input: '',
    })

    this.later(() => {
      if (hearts <= 0) {
        this.finish()
        return
      }
      if (q.mode === 'race') {
        this.set({ fb: null, locked: false, q: this.makeQuestion('race') })
        return
      }
      this.set({ fb: null, locked: false })
      // Клавиатура даёт вторую попытку на том же факте, остальное идёт дальше.
      if (q.mode !== 'keypad') this.next()
    }, FEEDBACK_BAD_MS)
  }

  // --------------------------------------------------------------- keypad

  typeDigit = (digit: string): void => {
    if (this.state.locked) return
    beep(440 + Number(digit) * 30, 0.05)
    this.set({
      input: this.state.input.length < 3 ? this.state.input + digit : this.state.input,
    })
  }

  backspace = (): void => {
    if (this.state.locked) return
    beep(300, 0.05)
    this.set({ input: this.state.input.slice(0, -1) })
  }

  submitInput = (): void => {
    if (this.state.locked || !this.state.input) return
    this.grade(this.state.input)
  }

  // ---------------------------------------------------------------- matching

  tapMatchLeft = (index: number): void => {
    if (this.state.locked || this.state.mDone.includes(index)) return
    beep(520, 0.06)
    this.set({ mLeft: index })
  }

  tapMatchRight = (value: number): void => {
    const { q, mLeft, locked } = this.state
    if (!q || locked || mLeft === null) return
    const pair = q.pairs[mLeft]
    if (!pair) return

    if (pair.r === value) {
      const mDone = [...this.state.mDone, mLeft]
      beep(760, 0.1)
      this.setProgress({ mastery: this.bumpMastery(factKey(pair.a, pair.b), true) })
      this.set({
        mDone,
        mLeft: null,
        right: this.state.right + 1,
        sessionXp: this.state.sessionXp + 8,
      })
      if (mDone.length === q.pairs.length) {
        this.set({ fb: 'ok', lastXp: 20, locked: true })
        this.later(() => this.next(), 800)
      }
      return
    }

    buzz()
    const key = factKey(pair.a, pair.b)
    this.setProgress({ mastery: this.bumpMastery(key, false) })
    this.set({
      mLeft: null,
      wrong: this.state.wrong + 1,
      combo: 0,
      tricky: this.state.tricky.includes(key) ? this.state.tricky : [...this.state.tricky, key],
    })
  }
}

export const gameStore = new GameStore()
